import { useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2, Minus, Plus, RotateCcw, X } from "lucide-react";

/**
 * Wraps a rendered visual (lineage graph, dependency tree, anything
 * coming back as PNG) with mouse-wheel zoom and click-drag pan.
 * Native DOM events; no library dependency.
 *
 * <p>The zoom/pan transform applies to the inner {@code <img>} via
 * CSS, so the wrapper container stays the same size in the document
 * flow — the image scales WITHIN its frame rather than blowing up the
 * chat layout. Wheel anchors at the cursor (the pixel under the mouse
 * stays under the mouse as you zoom). Drag pans freely.
 *
 * <p>The toolbar offers the usual three controls — fit, +, − — and a
 * reset shortcut. On touch devices, two-finger pinch-zoom is delegated
 * to the browser's native gesture handling on the {@code <img>} alone
 * (we don't override touchmove, so the browser does its thing).
 */
export function ZoomablePanImage({
  src,
  alt,
  className = "",
}: {
  src: string;
  alt?: string;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const dragRef = useRef<{ startX: number; startY: number; baseTx: number; baseTy: number } | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  // Esc closes the fullscreen overlay. Browsers block opening data:
  // URLs in a new tab (Chrome refuses to navigate top-level to a data
  // URL for security reasons), so the "open full-size" path is an
  // in-page modal instead — same component, bigger viewport, same
  // zoom/pan controls.
  useEffect(() => {
    if (!fullscreen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setFullscreen(false);
    }
    window.addEventListener("keydown", onKey);
    // Lock body scroll while overlay is open so wheel-zoom inside the
    // modal doesn't leak out to the page underneath.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [fullscreen]);

  // Wheel-zoom anchored at the cursor: the pixel under the mouse stays
  // under the mouse. Done by adjusting the translation by the cursor's
  // offset before/after the scale change.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    function onWheel(e: WheelEvent) {
      // Only intercept when Ctrl/Cmd held OR the cursor is inside the
      // image — otherwise let the page scroll naturally. The behavior
      // here matches Figma / Miro: hover → wheel zooms, scroll wheel
      // outside the canvas scrolls the page.
      if (!el) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      // Negative deltaY → zoom in
      const factor = Math.exp(-e.deltaY * 0.0015);
      setScale((prev) => {
        const next = clamp(prev * factor, 0.25, 8);
        const ratio = next / prev;
        setTx((x) => mouseX - (mouseX - x) * ratio);
        setTy((y) => mouseY - (mouseY - y) * ratio);
        return next;
      });
    }
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // Re-run when fullscreen toggles so the listener follows the active
    // container (inline → modal). Both versions use the same ref; React
    // points it at whichever element rendered last, and this effect
    // re-binds wheel to the correct one.
  }, [fullscreen]);

  function onMouseDown(e: React.MouseEvent) {
    if (e.button !== 0) return; // left button only
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseTx: tx, baseTy: ty };
    e.preventDefault();
  }
  function onMouseMove(e: React.MouseEvent) {
    const d = dragRef.current;
    if (!d) return;
    setTx(d.baseTx + (e.clientX - d.startX));
    setTy(d.baseTy + (e.clientY - d.startY));
  }
  function onMouseUp() {
    dragRef.current = null;
  }
  function onMouseLeave() {
    dragRef.current = null;
  }

  function reset() {
    setScale(1);
    setTx(0);
    setTy(0);
  }
  function zoomBy(factor: number) {
    setScale((prev) => clamp(prev * factor, 0.25, 8));
  }

  const toolbar = (
    <div className="pointer-events-none absolute bottom-2 right-2 flex items-center gap-1">
      <div className="pointer-events-auto flex items-center gap-1 rounded-md border border-datumcraft-slate/30 bg-white/95 px-1 py-0.5 shadow-sm backdrop-blur">
        <button
          type="button"
          onClick={() => zoomBy(0.8)}
          title="Zoom out"
          className="inline-flex h-7 w-7 items-center justify-center rounded text-datumcraft-slate hover:bg-datumcraft-mist hover:text-datumcraft-navy"
        >
          <Minus size={14} aria-hidden />
        </button>
        <span className="px-1 text-2xs tabular-nums text-datumcraft-slate" title={`scale ${scale.toFixed(2)}×`}>
          {Math.round(scale * 100)}%
        </span>
        <button
          type="button"
          onClick={() => zoomBy(1.25)}
          title="Zoom in"
          className="inline-flex h-7 w-7 items-center justify-center rounded text-datumcraft-slate hover:bg-datumcraft-mist hover:text-datumcraft-navy"
        >
          <Plus size={14} aria-hidden />
        </button>
        <span className="mx-1 h-4 w-px bg-datumcraft-slate/30" aria-hidden />
        <button
          type="button"
          onClick={reset}
          title="Reset zoom + pan"
          className="inline-flex h-7 w-7 items-center justify-center rounded text-datumcraft-slate hover:bg-datumcraft-mist hover:text-datumcraft-navy"
        >
          <RotateCcw size={13} aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => {
            // Reset zoom on entry/exit so the modal opens at 100%.
            reset();
            setFullscreen((v) => !v);
          }}
          title={fullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-datumcraft-slate hover:bg-datumcraft-mist hover:text-datumcraft-navy"
        >
          {fullscreen ? <Minimize2 size={13} aria-hidden /> : <Maximize2 size={13} aria-hidden />}
        </button>
      </div>
    </div>
  );

  const imageStyle: React.CSSProperties = {
    transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
    transition: dragRef.current ? "none" : "transform 60ms ease-out",
    willChange: "transform",
  };

  return (
    <>
      <div className={`relative overflow-hidden rounded-lg border border-datumcraft-slate/25 bg-white ${className}`}>
        <div
          ref={containerRef}
          className="relative h-[360px] w-full select-none"
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
          style={{ cursor: dragRef.current ? "grabbing" : "grab" }}
        >
          <img
            src={src}
            alt={alt ?? "Rendered visual"}
            draggable={false}
            className="block h-auto max-w-none origin-top-left"
            style={imageStyle}
          />
        </div>
        {!fullscreen && toolbar}
      </div>

      {fullscreen && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/85 backdrop-blur"
          role="dialog"
          aria-modal="true"
          aria-label="Fullscreen view"
        >
          {/* Close button — top-right, separate from the zoom toolbar */}
          <button
            type="button"
            onClick={() => setFullscreen(false)}
            title="Close (Esc)"
            className="absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/20 bg-white/10 text-white hover:bg-white/20"
          >
            <X size={18} aria-hidden />
          </button>

          <div
            ref={containerRef}
            className="relative flex-1 select-none overflow-hidden"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
            // Click on the dark backdrop closes the modal; click on the
            // image is consumed by the drag handler before it bubbles.
            onClick={(e) => {
              if (e.target === e.currentTarget) setFullscreen(false);
            }}
            style={{ cursor: dragRef.current ? "grabbing" : "grab" }}
          >
            <img
              src={src}
              alt={alt ?? "Rendered visual"}
              draggable={false}
              className="block h-auto max-w-none origin-top-left"
              style={imageStyle}
            />
          </div>
          {toolbar}
        </div>
      )}
    </>
  );
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}
